(() => {
  if (customElements.get('pml-dmc-chart')) return;
  const rgb = hex => hex.match(/[a-f\d]{2}/gi).map(n => parseInt(n, 16));
  const lab = hex => {
    const [r, g, b] = rgb(hex).map(n => { const v = n / 255; return v > .04045 ? ((v + .055) / 1.055) ** 2.4 : v / 12.92; });
    const f = v => v > .008856 ? Math.cbrt(v) : 7.787 * v + 16 / 116;
    const x = f((r * .4124564 + g * .3575761 + b * .1804375) / .95047);
    const y = f(r * .2126729 + g * .7151522 + b * .072175);
    const z = f((r * .0193339 + g * .119192 + b * .9503041) / 1.08883);
    return [116 * y - 16, 500 * (x - y), 200 * (y - z)];
  };
  class DmcChart extends HTMLElement {
    connectedCallback() {
      if (this.initialized) {
        window.addEventListener('beforeprint', this.beforePrint);
        window.addEventListener('afterprint', this.afterPrint);
        return;
      }
      this.initialized = true;
      this.$ = selector => this.querySelector(selector);
      this.grid = this.$('[data-grid]');
      this.cards = [...this.grid.children].map((node, index) => ({node, index, ...node.dataset, lab: lab(node.dataset.hex)}));
      this.family = 'all';
      this.pageSize = 49;
      this.search = this.$('[data-search]');
      this.sort = this.$('[data-sort]');
      this.picker = this.$('[data-picker]');
      this.hex = this.$('input[data-hex]');
      this.search.addEventListener('input', () => this.filter());
      this.sort.addEventListener('change', () => this.filter());
      this.$('[data-filters]').addEventListener('click', e => {
        const button = e.target.closest('[data-family]');
        if (!button) return;
        this.family = button.dataset.family;
        this.querySelectorAll('[data-filters] button').forEach(b => b.setAttribute('aria-pressed', String(b === button)));
        this.filter();
      });
      this.$('[data-reset]').addEventListener('click', () => {
        this.search.value = '';
        this.family = 'all';
        this.querySelectorAll('[data-filters] button').forEach(b => b.setAttribute('aria-pressed', String(b.dataset.family === 'all')));
        this.filter();
        this.search.focus();
      });
      this.$('[data-print]').addEventListener('click', () => window.print());
      this.$('[data-download]').addEventListener('click', () => this.download());
      this.$('[data-more]').addEventListener('click', () => {
        const firstNew = this.visible[this.limit];
        this.limit += this.pageSize;
        this.display();
        firstNew?.node.querySelector('button').focus({preventScroll: true});
      });
      this.beforePrint = () => { this.cards.forEach(c => { c.node.hidden = !this.visible.includes(c); }); };
      this.afterPrint = () => this.display();
      window.addEventListener('beforeprint', this.beforePrint);
      window.addEventListener('afterprint', this.afterPrint);
      this.$('[data-match-form]').addEventListener('submit', e => {
        e.preventDefault();
        let value = this.hex.value.trim();
        if (!value.startsWith('#')) value = '#' + value;
        if (/^#[a-f\d]{3}$/i.test(value)) value = '#' + value.slice(1).split('').map(c => c + c).join('');
        if (!/^#[a-f\d]{6}$/i.test(value)) {
          this.hex.setAttribute('aria-invalid', 'true');
          this.$('[data-hex-error]').hidden = false;
          this.hex.focus();
          return;
        }
        this.match(value);
      });
      this.picker.addEventListener('input', () => this.match(this.picker.value));
      this.addEventListener('click', e => {
        const card = e.target.closest('[data-color]');
        if (!card || !this.contains(card)) return;
        this.match(card.dataset.hex);
        this.$('[data-matcher]').scrollIntoView({behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth', block: 'start'});
        this.hex.focus({preventScroll: true});
      });
      this.$('[data-photo]').addEventListener('change', e => this.loadPhoto(e.target.files[0]));
      this.$('[data-canvas]').addEventListener('click', e => {
        const canvas = this.$('[data-canvas]');
        const rect = canvas.getBoundingClientRect();
        this.sample(Math.min(canvas.width - 1, Math.floor((e.clientX - rect.left) * canvas.width / rect.width)), Math.min(canvas.height - 1, Math.floor((e.clientY - rect.top) * canvas.height / rect.height)));
      });
      this.$('[data-canvas]').addEventListener('keydown', e => {
        if (!['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Enter', ' '].includes(e.key)) return;
        e.preventDefault();
        const canvas = this.$('[data-canvas]');
        const step = e.shiftKey ? 20 : 5;
        if (e.key === 'ArrowLeft') this.photoX -= step;
        if (e.key === 'ArrowRight') this.photoX += step;
        if (e.key === 'ArrowUp') this.photoY -= step;
        if (e.key === 'ArrowDown') this.photoY += step;
        this.sample(Math.max(0, Math.min(canvas.width - 1, this.photoX)), Math.max(0, Math.min(canvas.height - 1, this.photoY)));
      });
      this.classList.add('pml-dmc--ready');
      this.filter();
      this.match(this.hex.value);
    }
    disconnectedCallback() {
      window.removeEventListener('beforeprint', this.beforePrint);
      window.removeEventListener('afterprint', this.afterPrint);
    }
    filter() {
      const query = this.search.value.trim().toLowerCase().replace(/^dmc\s*/, '');
      this.visible = this.cards.filter(c => (this.family === 'all' || c.family === this.family) && (`${c.code} ${c.name} ${c.hex}`.toLowerCase().includes(query)));
      const order = this.sort.value;
      const compare = order === 'name' ? (a, b) => a.name.localeCompare(b.name) : order === 'number' ? (a, b) => a.code.localeCompare(b.code, undefined, {numeric: true}) : (a, b) => a.index - b.index;
      this.visible.sort(compare).forEach(c => this.grid.append(c.node));
      this.limit = this.pageSize;
      this.display();
      this.$('[data-empty]').hidden = this.visible.length !== 0;
      this.$('[data-reset]').hidden = !query && this.family === 'all';
      this.$('[data-download]').disabled = this.visible.length === 0;
    }
    display() {
      const shown = new Set(this.visible.slice(0, this.limit));
      this.cards.forEach(c => { c.node.hidden = !shown.has(c); });
      this.$('[data-count]').textContent = this.$('[data-count]').dataset.template.replace('[shown]', shown.size).replace('[count]', this.visible.length).replace('[total]', this.cards.length);
      this.$('[data-more]').hidden = this.limit >= this.visible.length;
    }
    match(value) {
      value = value.toUpperCase();
      this.hex.value = value;
      this.picker.value = value;
      this.hex.removeAttribute('aria-invalid');
      this.$('[data-hex-error]').hidden = true;
      const target = lab(value);
      const sorted = [...this.cards].sort((a, b) => this.distance(a.lab, target) - this.distance(b.lab, target)).slice(0, 6);
      this.$('[data-matches]').replaceChildren(...sorted.map(c => c.node.cloneNode(true)));
      this.$('[data-matches]').querySelectorAll('[hidden]').forEach(n => { n.hidden = false; });
      this.$('[data-match-status]').textContent = this.$('[data-match-status]').dataset.template.replace('[hex]', value).replace('[code]', sorted[0].code).replace('[name]', sorted[0].name);
    }
    distance(a, b) { return a.reduce((sum, n, i) => sum + (n - b[i]) ** 2, 0); }
    download() {
      const quote = s => '"' + String(s).replaceAll('"', '""') + '"';
      const lines = [['DMC', 'Name', 'Hex', 'Red', 'Green', 'Blue'], ...this.visible.map(c => [c.code, c.name, c.hex, ...rgb(c.hex)]), [], ['Color data: craft-color-codes by MakeBead', 'https://makebead.com/', 'CC BY 4.0', 'https://creativecommons.org/licenses/by/4.0/'], ['Adapted by Paint Me Like: filtering and sorting. Colors are approximate.']];
      const url = URL.createObjectURL(new Blob(['\uFEFF' + lines.map(row => row.map(quote).join(',')).join('\r\n')], {type: 'text/csv;charset=utf-8'}));
      const link = document.createElement('a');
      link.href = url;
      link.download = 'paint-me-like-dmc-color-chart.csv';
      link.click();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    }
    async loadPhoto(file) {
      if (!file) return;
      const error = this.$('[data-photo-error]');
      const canvas = this.$('[data-canvas]');
      error.hidden = true;
      canvas.hidden = true;
      this.$('[data-photo-help]').hidden = true;
      const version = this.photoVersion = (this.photoVersion || 0) + 1;
      if (!['image/jpeg', 'image/png', 'image/webp'].includes(file.type) || file.size > 15 * 1024 * 1024) { error.hidden = false; return; }
      const url = URL.createObjectURL(file);
      try {
        const photo = new Image();
        photo.src = url;
        await photo.decode();
        if (version !== this.photoVersion) return;
        const scale = Math.min(1, 800 / photo.naturalWidth, 500 / photo.naturalHeight);
        canvas.width = Math.max(1, Math.round(photo.naturalWidth * scale));
        canvas.height = Math.max(1, Math.round(photo.naturalHeight * scale));
        const ctx = canvas.getContext('2d', {willReadFrequently: true});
        ctx.fillStyle = '#FFFFFF';
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        ctx.drawImage(photo, 0, 0, canvas.width, canvas.height);
        this.photoPixels = ctx.getImageData(0, 0, canvas.width, canvas.height);
        canvas.hidden = false;
        this.$('[data-photo-help]').hidden = false;
        this.sample(Math.floor(canvas.width / 2), Math.floor(canvas.height / 2));
      } catch { if (version === this.photoVersion) error.hidden = false; }
      finally { URL.revokeObjectURL(url); }
    }
    sample(x, y) {
      if (!this.photoPixels) return;
      this.photoX = x;
      this.photoY = y;
      const canvas = this.$('[data-canvas]');
      const ctx = canvas.getContext('2d');
      const i = (y * canvas.width + x) * 4;
      const value = '#' + [...this.photoPixels.data.slice(i, i + 3)].map(n => n.toString(16).padStart(2, '0')).join('');
      this.match(value);
      ctx.putImageData(this.photoPixels, 0, 0);
      ctx.beginPath(); ctx.arc(x, y, 7, 0, Math.PI * 2); ctx.lineWidth = 3; ctx.strokeStyle = '#FFFFFF'; ctx.stroke();
      ctx.beginPath(); ctx.arc(x, y, 9, 0, Math.PI * 2); ctx.lineWidth = 1; ctx.strokeStyle = '#30223F'; ctx.stroke();
    }
  }
  customElements.define('pml-dmc-chart', DmcChart);
})();
